import { loadedScriptSelector } from '@skippay/core/modules/externalScripts';
import { LOW_PRIORITY, loadExternalScript } from '@skippay/core/utils/domUtils';

// TODO: unused constants serves as memo that such scripts are loaded and might be loaded via this
//  module in future
export const EXPONEA = 'exponea';
export const DAKTELA_CHAT = 'daktelaChat';
export const FOXENTRY = 'foxentry';
export const GOOGLE_MAPS = 'googleMaps';
// currently loaded via analytics module
export const GOOGLE_TAG_MANAGER = 'googleTagManager';
export const ONE_SIGNAL = 'oneSignal';
// currently loaded via sentry module
export const SENTRY = 'sentry';
export const SMARTFORM = 'smartform';

export const foxentryLoadedSelector = loadedScriptSelector(FOXENTRY);
export const googleMapsLoadedSelector = loadedScriptSelector(GOOGLE_MAPS);

/**
 * @type {Record<string, ExternalScriptConfiguration>}
 * @typedef ExternalScriptConfiguration
 * @type object
 * @property {function(): Promise<void>} [init] - any post-load initialization for the script, will be run after `load`
 *   fulfills. Expects Promise API. In some cases, the init must be set prior load, and fulfills later (e.g. the
 *   property `facebookAsyncInit` shall be set on the window before the Facebook SDK is loaded), we can utilize
 *   lodash/constant for that, which creates Promise and window callback immediately and the promise fulfills with
 *   initialization later. See DAKTELA_CHAT for example.
 * @property {boolean} [lazy] - lazy scripts are not loaded on application load, but on demand.
 * @property {function(): Promise<void>} load - callback to load the script with promise API.
 * @property {boolean} [securedOnly] - trusted scripts are, by default, loaded on _all_ pages, setting this to `true`
 * 	will load the script on secured pages only.
 * @property {boolean} [trusted] - untrusted scripts are not loaded on secured pages.
 */
const selfCareExternalScripts = {
	[EXPONEA]: {
		trusted: true,
		load: async ({ country }) => {
			const exponeaToken =
				country === 'CZ' ? process.env.EXPONEA_TOKEN : process.env.EXPONEA_TOKEN_SK;

			if (!exponeaToken) {
				throw new Error('Environment property "EXPONEA_TOKEN" not set.');
			}

			const options = {
				target: 'https://data-api.csob.cz',
				token: exponeaToken,
			};

			if (country === 'CZ') {
				options.track = {
					visits: true,
					google_analytics: false,
				};
			}

			if (country === 'SK') {
				options.experimental = {
					non_personalized_weblayers: true,
				};
			}

			// minified code template from Exponea
			/* eslint-disable */
			!(function (e, n, t, i, r, o) {
				function s(e) {
					if ('number' != typeof e) return e;
					var n = new Date();
					return new Date(n.getTime() + 1e3 * e);
				}

				var a = 4e3,
					c = 'xnpe_async_hide';

				function p(e) {
					return e.reduce(
						function (e, n) {
							return (
								(e[n] = function () {
									e._.push([n.toString(), arguments]);
								}),
								e
							);
						},
						{ _: [] }
					);
				}

				function m(e, n, t) {
					var i = t.createElement(n);
					i.src = e;
					var r = t.getElementsByTagName(n)[0];
					return r.parentNode.insertBefore(i, r), i;
				}

				function u(e) {
					return '[object Date]' === Object.prototype.toString.call(e);
				}

				(o.target = o.target || 'https://api.exponea.com'),
					(o.file_path = o.file_path || o.target + '/js/exponea.min.js'),
					(r[n] = p([
						'anonymize',
						'initialize',
						'identify',
						'getSegments',
						'update',
						'track',
						'trackLink',
						'trackEnhancedEcommerce',
						'getHtml',
						'showHtml',
						'showBanner',
						'showWebLayer',
						'ping',
						'getAbTest',
						'loadDependency',
						'getRecommendation',
						'reloadWebLayers',
						'_preInitialize',
						'_initializeConfig',
					])),
					(r[n].notifications = p(['isAvailable', 'isSubscribed', 'subscribe', 'unsubscribe'])),
					(r[n].segments = p(['subscribe'])),
					(r[n]['snippetVersion'] = 'v2.7.0'),
					(function (e, n, t) {
						(e[n]['_' + t] = {}),
							(e[n]['_' + t].nowFn = Date.now),
							(e[n]['_' + t].snippetStartTime = e[n]['_' + t].nowFn());
					})(r, n, 'performance'),
					(function (e, n, t, i, r, o) {
						e[r] = {
							sdk: e[i],
							sdkObjectName: i,
							skipExperiments: !!t.new_experiments,
							sign: t.token + '/' + (o.exec(n.cookie) || ['', 'new'])[1],
							path: t.target,
						};
					})(r, e, o, n, i, RegExp('__exponea_etc__' + '=([\\w-]+)')),
					(function (e, n, t) {
						m(e.file_path, n, t);
					})(o, t, e),
					(function (e, n, t, i, r, o, p) {
						if (e.new_experiments) {
							!0 === e.new_experiments && (e.new_experiments = {});
							var l,
								f = e.new_experiments.hide_class || c,
								_ = e.new_experiments.timeout || a,
								g = encodeURIComponent(o.location.href.split('#')[0]);
							e.cookies &&
								e.cookies.expires &&
								('number' == typeof e.cookies.expires || u(e.cookies.expires)
									? (l = s(e.cookies.expires))
									: e.cookies.expires.tracking &&
										('number' == typeof e.cookies.expires.tracking ||
											u(e.cookies.expires.tracking)) &&
										(l = s(e.cookies.expires.tracking))),
								l && l < new Date() && (l = void 0);
							var d =
								e.target +
								'/webxp/' +
								n +
								'/' +
								o[t].sign +
								'/modifications.min.js?http-referer=' +
								g +
								'&timeout=' +
								_ +
								'ms' +
								(l ? '&cookie-expires=' + Math.floor(l.getTime() / 1e3) : '');
							'sync' === e.new_experiments.mode &&
							o.localStorage.getItem('__exponea__sync_modifications__')
								? (function (e, n, t, i, r) {
										(t[r][n] = '<' + n + ' src="' + e + '"></' + n + '>'),
											i.writeln(t[r][n]),
											i.writeln(
												'<' +
													n +
													'>!' +
													r +
													'.init && document.writeln(' +
													r +
													'.' +
													n +
													'.replace("/' +
													n +
													'/", "/' +
													n +
													'-async/").replace("><", " async><"))</' +
													n +
													'>'
											);
									})(d, n, o, p, t)
								: (function (e, n, t, i, r, o, s, a) {
										o.documentElement.classList.add(e);
										var c = m(t, i, o);

										function p() {
											r[a].init || m(t.replace('/' + i + '/', '/' + i + '-async/'), i, o);
										}

										function u() {
											o.documentElement.classList.remove(e);
										}

										(c.onload = p), (c.onerror = p), r.setTimeout(u, n), (r[s]._revealPage = u);
									})(f, _, d, n, o, p, r, t);
						}
					})(o, t, i, 0, n, r, e),
					(function (e, n, t) {
						var i;
						e[n]._initializeConfig(t),
							(null === (i = t.experimental) || void 0 === i
								? void 0
								: i.non_personalized_weblayers) && e[n]._preInitialize(t),
							(e[n].start = function (i) {
								i &&
									Object.keys(i).forEach(function (e) {
										return (t[e] = i[e]);
									}),
									e[n].initialize(t);
							});
					})(r, n, o);
			})(document, 'exponea', 'script', 'webxpClient', window, options);
			exponea.start();
			/* eslint-enable */
		},
	},
	[DAKTELA_CHAT]: {
		trusted: true,
		lazy: true,
		load: async () => {
			if (!process.env.DAKTELA_SERVER_URL || !process.env.DAKTELA_ACCESS_TOKEN) {
				throw new Error(
					'Environment properties "DAKTELA_SERVER_URL" or "DAKTELA_ACCESS_TOKEN" not set.'
				);
			}

			return loadExternalScript({
				id: 'daktela-chat',
				src: `${process.env.DAKTELA_SERVER_URL}external/web/web.js`,
				async: true,
				priority: LOW_PRIORITY,
			});
		},
		init: () => {
			return new Promise((resolve, reject) => {
				const daktelaGuiConfig = {
					accessToken: process.env.DAKTELA_ACCESS_TOKEN,
					server: process.env.DAKTELA_SERVER_URL,
				};
				if (!window.daktelaGui) {
					reject(new Error('DAKTELA_CHAT script not loaded.'));
					return;
				}

				// eslint-disable-next-line new-cap, no-undef
				const daktelaWeb = new daktelaGui();

				daktelaWeb.init(daktelaGuiConfig);
				resolve();
			});
		},
	},
	/** field typeahead and validation for address and other public accessible registers */
	[FOXENTRY]: {
		trusted: true,
		load: async () => {
			if (!process.env.FOXENTRY_SCRIPT_CODE) {
				throw new Error('Environment property "FOXENTRY_SCRIPT_CODE" not set.');
			}
			return loadExternalScript({ src: 'https://cdn.foxentry.cz/lib' });
		},
		init: () => {
			const initialize = new Promise((resolve) => {
				window.onFoxentryProjectLoad = () => {
					// prevent scroll to focused element in DOM
					window.FoxentryBuilder?.setOption('inputScrollEnabled', false);
					resolve();
				};
			});
			window.Foxentry = new window.FoxentryBase(process.env.FOXENTRY_SCRIPT_CODE);
			return initialize;
		},
		securedOnly: true,
	},
	[SMARTFORM]: {
		trusted: true,
		load: async () => {
			if (!process.env.SMARTFORM_ID) {
				throw new Error('Environment property "SMARTFORM_ID" not set.');
			}
			return loadExternalScript({ src: 'https://client.smartform.cz/v2/smartform.js' });
		},
		init: () =>
			new Promise((resolve) => {
				window.smartform.beforeInit = () => {
					window.smartform.setClientId(process.env.SMARTFORM_ID);
					resolve();
				};
			}),
		securedOnly: true,
	},
	// Temporary disabled due to bad token on web
	// /** push-notifications */
	// [ONE_SIGNAL]: {
	// 	trusted: false,
	// 	load: async () => {
	// 		if (!process.env.PUSH_NOTIFICATION_ONESIGNAL_APP_ID) {
	// 			throw new Error('Environment property "PUSH_NOTIFICATION_ONESIGNAL_APP_ID" not set.');
	// 		}
	// 		return loadExternalScript({
	// 			src: 'https://cdn.onesignal.com/sdks/OneSignalSDK.js',
	// 			priority: LOW_PRIORITY,
	// 		});
	// 	},
	// 	init: () =>
	// 		new Promise((resolve) => {
	// 			window.OneSignal.init({ appId: process.env.PUSH_NOTIFICATION_ONESIGNAL_APP_ID });
	// 			resolve();
	// 		}),
	// },
	[GOOGLE_MAPS]: {
		trusted: true,
		load: async () => {
			if (!process.env.GOOGLE_MAPS_API_KEY) {
				throw new Error('Environment property "GOOGLE_MAPS_API_KEY" not set.');
			}

			return loadExternalScript({
				src: `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_API_KEY}&libraries=places`,
			});
		},
		securedOnly: true,
	},
};

export default selfCareExternalScripts;
